import type { SVGProps } from 'react';
import { MotionConfig } from 'framer-motion';

import { motion } from '@/components/public/PublicMotion';
import { PenUnderline } from '@/components/public/PenUnderline';
import { PublicDemoLink } from '@/components/public/PublicDemoLink';
import { PublicLayout } from '@/components/public/PublicLayout';
import { PublicSeo } from '@/components/public/PublicSeo';
import { SectionEyebrow } from '@/components/public/SectionEyebrow';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Heading } from '@/components/ui/heading';
import {
  fadeInUp,
  listReveal,
  revealTransition,
  sectionReveal,
  staggerContainer,
} from '@/hooks/use-scroll-animation';

const penIconProps = {
  viewBox: '0 0 48 48',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2.5,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': true,
} satisfies SVGProps<SVGSVGElement>;

const CrossedCircleIcon = ({ className }: { className?: string }) => (
  <svg {...penIconProps} className={className}>
    <circle cx="24" cy="24" r="15" />
    <path d="M14 14 L34 34" />
  </svg>
);

const LockIcon = ({ className }: { className?: string }) => (
  <svg {...penIconProps} className={className}>
    <rect x="10" y="20" width="28" height="20" rx="5" />
    <path d="M16 20 V15 a8 8 0 0 1 16 0 V20" />
    <circle cx="24" cy="30" r="2" fill="currentColor" stroke="none" />
  </svg>
);

const NoTrainingIcon = ({ className }: { className?: string }) => (
  <svg {...penIconProps} className={className}>
    <path d="M10 14 C18 10 30 10 38 14 V28 C38 34 32 38 24 41 C16 38 10 34 10 28 Z" />
    <path d="M16 24 L32 24" opacity={0.5} />
    <path d="M12 10 L36 38" />
  </svg>
);

const DocumentIcon = ({ className }: { className?: string }) => (
  <svg {...penIconProps} className={className}>
    <path d="M12 12 H30 L38 20 V38 a2 2 0 0 1 -2 2 H12 a2 2 0 0 1 -2 -2 V14 a2 2 0 0 1 2 -2 Z" />
    <path d="M30 12 V20 H38" opacity={0.5} />
    <path d="M17 28 L31 28" strokeDasharray="2 5" />
    <path d="M17 33 L27 33" strokeDasharray="2 5" />
  </svg>
);

const safetySteps = [
  {
    number: '01',
    title: 'Controllo automatico in tempo reale',
    body: 'Ogni messaggio viene analizzato per rilevare possibili segnali di rischio, come ideazione suicidaria, autolesionismo o crisi gravi. Il controllo riguarda sia ciò che scrive la persona sia le risposte dell’IA.',
  },
  {
    number: '02',
    title: 'Avviso immediato al Safety Team',
    body: 'Se viene rilevato un possibile pericolo, il Safety Team di Emovia riceve una notifica, valuta la situazione e stabilisce come intervenire.',
  },
  {
    number: '03',
    title: 'Blocco immediato della conversazione',
    body: 'Per precauzione, la conversazione viene interrotta subito. L’IA non prosegue in una situazione che richiede un intervento umano.',
  },
  {
    number: '04',
    title: 'Interviene il professionista',
    body: 'Ricevi una notifica e puoi gestire la situazione attraverso il canale clinico più appropriato. La tecnologia si ferma quando è necessario l’intervento del professionista.',
  },
] as const;

const aiBoundaries = [
  {
    title: 'Non diagnostica',
    body: 'Nessuna etichetta clinica, nessuna ipotesi diagnostica, mai.',
  },
  {
    title: 'Non consiglia',
    body: 'Non suggerisce cosa fare, non propone tecniche o esercizi non previsti da te.',
  },
  {
    title: 'Non asseconda',
    body: 'Non cerca di compiacere, non offre consolazione automatica e non simula un rapporto di amicizia.',
  },
  {
    title: 'Non gestisce le crisi',
    body: 'Di fronte a una crisi non improvvisa né tenta di gestirla: interrompe la conversazione e attiva il sistema di sicurezza.',
  },
] as const;

const privacyFeatures = [
  {
    icon: LockIcon,
    title: 'Dati crittografati',
    body: 'Tutti i contenuti sono crittografati sia durante la trasmissione sia quando vengono archiviati. Il trattamento dei dati rispetta le garanzie previste dal GDPR.',
  },
  {
    icon: NoTrainingIcon,
    title: 'Dati esclusi dall’addestramento',
    body: 'I contenuti elaborati tramite i fornitori di intelligenza artificiale non vengono usati per addestrare i loro modelli.',
  },
  {
    icon: DocumentIcon,
    title: 'Cancellazione e portabilità',
    body: 'Puoi chiedere la cancellazione dei dati o riceverne una copia. Ogni richiesta viene gestita secondo i tempi e le modalità indicati nell’informativa.',
  },
] as const;

const controlAreas = [
  {
    eyebrow: 'La persona decide',
    items: [
      'Cosa scrivere, quando farlo e con quale livello di dettaglio',
      'Quali contenuti condividere con lo psicologo, con la possibilità di revocare la condivisione in qualsiasi momento',
      'Se e quando chiedere la cancellazione dei propri dati',
    ],
    note: 'I contenuti non condivisi restano privati: nella dashboard dello psicologo risultano protetti e non sono leggibili.',
  },
  {
    eyebrow: 'Lo psicologo governa',
    items: [
      'L’attivazione: nessuno entra in Emovia senza una licenza assegnata da te',
      'Le istruzioni, le condizioni e i limiti dell’IA per ogni cliente',
      'Gli accessi, le revoche e la cancellazione dei dati del percorso',
    ],
    note: 'Puoi consultare i contenuti condivisi, ma non intervenire nella conversazione: il dialogo tra la persona e l’IA non è un canale di messaggistica con te.',
  },
] as const;

const ShieldIllustration = () => (
  <svg viewBox="0 0 280 280" className="h-auto w-full max-w-[280px]" fill="none" aria-hidden>
    <path
      d="M140 28 L226 56 V128 C226 184 192 218 140 244 C88 218 54 184 54 128 V56 Z"
      stroke="currentColor"
      strokeWidth={4}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="public-draw text-primary"
      style={
        {
          '--draw-length': '700',
          '--draw-duration': '1.6s',
          '--draw-delay': '0.3s',
        } as React.CSSProperties
      }
    />
    <path
      d="M100 138 L128 166 L184 102"
      stroke="currentColor"
      strokeWidth={5}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="public-draw text-orange"
      style={
        {
          '--draw-length': '130',
          '--draw-duration': '0.8s',
          '--draw-delay': '1.6s',
        } as React.CSSProperties
      }
    />
    <path
      d="M30 252 C80 244 140 250 196 246"
      stroke="currentColor"
      strokeWidth={2.5}
      strokeLinecap="round"
      strokeDasharray="2 8"
      className="text-gray-dark"
      opacity={0.7}
    />
  </svg>
);

const SafetyConnector = ({ flip }: { flip: boolean }) => (
  <svg viewBox="0 0 24 64" className="h-16 w-6 text-blue-accent" aria-hidden>
    <path
      d={flip ? 'M12 4 C18 20 6 40 12 60' : 'M12 4 C6 20 18 40 12 60'}
      fill="none"
      stroke="currentColor"
      strokeWidth={2.5}
      strokeLinecap="round"
      strokeDasharray="2 8"
    />
  </svg>
);

const Sicurezza = () => {
  return (
    <PublicLayout>
      <PublicSeo
        title="Emovia | Sicurezza e Privacy"
        description="Scopri come Emovia protegge i dati clinici con infrastruttura europea, crittografia, controllo degli accessi e supervisione dei sistemi AI."
        path="/sicurezza"
      />

      <MotionConfig reducedMotion="user">
        <section className="border-b border-border bg-background">
          <div className="container grid max-w-[1160px] gap-12 py-20 md:py-24 lg:grid-cols-[1.1fr_0.9fr] lg:items-center lg:gap-18">
            <motion.div
              initial="hidden"
              animate="visible"
              variants={staggerContainer}
              className="grid gap-6"
            >
              <motion.div variants={fadeInUp} transition={revealTransition}>
                <SectionEyebrow className="mb-5 text-primary">Sicurezza e privacy</SectionEyebrow>
                <Heading
                  level={1}
                  font="display"
                  className="max-w-[18ch] text-4xl text-primary-dark md:text-5xl"
                >
                  La sicurezza non si promette. Si <PenUnderline>progetta.</PenUnderline>
                </Heading>
              </motion.div>

              <motion.p
                variants={fadeInUp}
                transition={revealTransition}
                className="max-w-[54ch] text-lg text-gray-darker md:text-xl"
              >
                Quando proponi uno strumento digitale a una persona che segui, non basta chiedersi
                che cosa promette. Bisogna sapere che cosa accade se emerge un problema. Qui lo
                spieghiamo con chiarezza.
              </motion.p>

              <motion.nav
                variants={fadeInUp}
                transition={revealTransition}
                aria-label="Sezioni della pagina"
                className="flex flex-wrap gap-x-6 gap-y-3"
              >
                {[
                  { href: '#supervisione', label: 'Supervisione clinica' },
                  { href: '#privacy', label: 'Privacy e dati' },
                  { href: '#controllo', label: 'Chi controlla cosa' },
                ].map((link) => (
                  <a
                    key={link.href}
                    href={link.href}
                    className="border-b-2 border-blue-accent pb-0.5 font-semibold text-primary no-underline transition-colors hover:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    {link.label}
                  </a>
                ))}
              </motion.nav>
            </motion.div>

            <motion.div
              initial="hidden"
              animate="visible"
              variants={fadeInUp}
              transition={revealTransition}
              className="flex justify-center"
            >
              <ShieldIllustration />
            </motion.div>
          </div>
        </section>

        <section className="bg-secondary">
          <motion.div
            {...sectionReveal}
            className="container grid max-w-[880px] gap-4 py-16 md:py-18"
          >
            <SectionEyebrow className="text-orange-dark">Il punto di partenza</SectionEyebrow>
            <p className="text-xl font-semibold text-balance text-primary-dark md:text-2xl">
              Il dovere di tutela del benessere psicologico delle persone (art. 13 del Codice
              Deontologico) non viene meno quando si usa uno strumento digitale. Per questo, in
              Emovia, la sicurezza non è una funzione aggiunta in un secondo momento: guida la
              progettazione dell’intero servizio.
            </p>
            <p className="text-gray-darker">
              Circa il 37% degli adulti usa già chatbot generalisti per ricevere supporto emotivo,
              senza il controllo di un professionista. Emovia nasce anche per offrire a questo
              bisogno un contesto progettato per l’uso clinico.
            </p>
          </motion.div>
        </section>

        <section id="supervisione" className="scroll-mt-16 bg-primary-dark text-white">
          <div className="container max-w-[1160px] py-20 md:py-26">
            <motion.div {...sectionReveal} className="grid max-w-[760px] gap-3.5">
              <SectionEyebrow className="text-blue-accent">Supervisione clinica</SectionEyebrow>
              <Heading level={2} className="text-3xl font-bold text-white md:text-4xl">
                Cosa accade, passo dopo passo, se emerge un rischio.
              </Heading>
              <p className="max-w-[62ch] text-lg text-blue-light">
                Ogni interazione, sia della persona sia dell’IA, viene sottoposta a controlli
                automatici. Se viene rilevato un possibile segnale di rischio, si attiva in pochi
                istanti una procedura definita.
              </p>
            </motion.div>

            <motion.ol {...listReveal} className="mt-14 grid max-w-[820px]">
              {safetySteps.map((step, index) => {
                const isLast = index === safetySteps.length - 1;

                return (
                  <motion.li
                    key={step.number}
                    variants={fadeInUp}
                    transition={revealTransition}
                    className="grid grid-cols-[56px_1fr] gap-6"
                  >
                    <div className="grid justify-items-center">
                      <span
                        className={`flex h-14 w-14 items-center justify-center rounded-full font-accent font-bold ${
                          isLast ? 'bg-orange text-white' : 'bg-white text-primary'
                        }`}
                      >
                        {step.number}
                      </span>
                      {!isLast ? <SafetyConnector flip={index % 2 === 1} /> : null}
                    </div>
                    <div className={isLast ? '' : 'pb-7'}>
                      <p className="mb-2 text-lg font-bold text-white">{step.title}</p>
                      <p className="max-w-[58ch] text-blue-light">{step.body}</p>
                    </div>
                  </motion.li>
                );
              })}
            </motion.ol>

            <motion.div
              {...sectionReveal}
              className="mt-14 flex flex-wrap items-baseline gap-x-4 gap-y-2 border-t border-white/20 pt-7"
            >
              <p className="text-lg font-bold text-white">È questo che distingue Emovia.</p>
              <p className="text-blue-light">
                Controlli automatici, avvisi tempestivi e coinvolgimento del professionista.
              </p>
            </motion.div>
          </div>
        </section>

        <section className="border-b border-border bg-background">
          <div className="container max-w-[1160px] py-20 md:py-24">
            <motion.div {...sectionReveal} className="mb-12 grid max-w-[720px] gap-3.5">
              <SectionEyebrow className="text-magenta">Limiti definiti dall’inizio</SectionEyebrow>
              <Heading level={2} className="text-3xl font-bold text-primary-dark md:text-4xl">
                Essere sicura significa anche sapere cosa l’IA non deve fare.
              </Heading>
              <p className="text-lg text-gray-darker">
                Questi limiti non sono filtri aggiunti in un secondo momento: fanno parte della
                progettazione. L’IA Interrogativa è progettata per restare entro questo perimetro,
                anche quando la persona le chiede di superarlo.
              </p>
            </motion.div>

            <motion.div {...listReveal} className="grid gap-7 sm:grid-cols-2 lg:grid-cols-4">
              {aiBoundaries.map((boundary) => (
                <motion.div
                  key={boundary.title}
                  variants={fadeInUp}
                  transition={revealTransition}
                  className="border-t-2 border-magenta pt-4"
                >
                  <CrossedCircleIcon className="mb-2.5 h-8 w-8 text-magenta opacity-85" />
                  <p className="mb-1.5 font-bold text-primary-dark">{boundary.title}</p>
                  <p className="text-sm leading-relaxed text-gray-darker">{boundary.body}</p>
                </motion.div>
              ))}
            </motion.div>

            <motion.p {...sectionReveal} className="mt-10 text-lg font-semibold text-primary-dark">
              Quando serve, ricorda alla persona: <em>«Non sostituisco il tuo terapeuta»</em>.
            </motion.p>
          </div>
        </section>

        <section id="privacy" className="scroll-mt-16 bg-blue-lighter">
          <div className="container max-w-[1160px] py-20 md:py-26">
            <motion.div {...sectionReveal} className="mb-14 grid max-w-[760px] gap-3.5">
              <SectionEyebrow className="text-primary">Privacy e dati</SectionEyebrow>
              <Heading level={2} className="text-3xl font-bold text-primary-dark md:text-4xl">
                La privacy è parte del prodotto, non solo dell’informativa.
              </Heading>
              <p className="max-w-[62ch] text-lg text-gray-darker">
                I contenuti affidati a Emovia possono includere informazioni personali e sanitarie
                particolarmente sensibili. Per questo vengono trattati con protezioni adeguate fin
                dalla progettazione.
              </p>
            </motion.div>

            <motion.div {...listReveal} className="grid gap-6 md:grid-cols-3">
              {privacyFeatures.map((feature) => (
                <motion.div key={feature.title} variants={fadeInUp} transition={revealTransition}>
                  <Card className="h-full rounded-lg border-border bg-card shadow-card">
                    <CardHeader className="pb-2">
                      <feature.icon className="mb-2 h-9 w-9 text-primary" />
                      <Heading level={3} className="text-primary-dark">
                        {feature.title}
                      </Heading>
                    </CardHeader>
                    <CardContent>
                      <p className="text-gray-darker">{feature.body}</p>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        <section id="controllo" className="scroll-mt-16 bg-background">
          <div className="container max-w-[1160px] py-20 md:py-24">
            <motion.div {...sectionReveal} className="mb-14 grid max-w-[720px] gap-3.5">
              <SectionEyebrow className="text-primary">Chi controlla cosa</SectionEyebrow>
              <Heading level={2} className="text-3xl font-bold text-primary-dark md:text-4xl">
                Scelte chiare, senza automatismi nascosti.
              </Heading>
            </motion.div>

            <motion.div {...listReveal} className="grid gap-6 md:grid-cols-2">
              {controlAreas.map((area) => (
                <motion.div key={area.eyebrow} variants={fadeInUp} transition={revealTransition}>
                  <Card className="h-full rounded-3xl border-border bg-card shadow-card">
                    <CardHeader>
                      <SectionEyebrow className="text-primary">{area.eyebrow}</SectionEyebrow>
                    </CardHeader>
                    <CardContent className="grid gap-4">
                      <ul className="grid gap-3">
                        {area.items.map((item) => (
                          <li key={item} className="flex gap-3 text-gray-darker">
                            <span aria-hidden className="font-bold text-primary">
                              —
                            </span>
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                      <p className="border-t border-border pt-3.5 text-sm text-muted-foreground">
                        {area.note}
                      </p>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </motion.div>

            <motion.p
              {...sectionReveal}
              className="mt-10 max-w-[70ch] text-lg font-semibold text-primary-dark"
            >
              La persona sa quali contenuti sono visibili; tu sai quali aspetti puoi gestire.
              Responsabilità e possibilità di controllo restano chiare per entrambi.
            </motion.p>
          </div>
        </section>

        <section className="bg-primary-dark">
          <motion.div
            {...sectionReveal}
            className="container grid max-w-[880px] justify-items-center gap-5 py-20 text-center md:py-22"
          >
            <Heading level={2} className="max-w-[26ch] text-3xl font-bold text-white md:text-4xl">
              Vuoi vedere come funzionano limiti, accessi e privacy?
            </Heading>
            <p className="max-w-[52ch] text-lg text-blue-light">
              Durante la demo ti mostriamo dove si ferma l’IA, quali contenuti può vedere lo
              psicologo e quali restano privati. In modo concreto, senza formule vaghe.
            </p>
            <Button
              size="lg"
              asChild
              className="mt-1 h-12 bg-white px-8 text-base font-bold text-primary shadow-md hover:bg-blue-lighter"
            >
              <PublicDemoLink location="sicurezza_final_cta">Richiedi una demo</PublicDemoLink>
            </Button>
          </motion.div>
        </section>
      </MotionConfig>
    </PublicLayout>
  );
};

export default Sicurezza;
